/**
 * Detects a local copy of the interface — the downloadable local server,
 * listening on the reader's own computer — by reading its `/version.json`.
 *
 * The probe never throws and never waits longer than its timeout. A refused
 * connection, a timeout, a non-200 answer, and an answer of the wrong shape
 * all mean the same thing to this page: no local copy to offer.
 */
import { z } from 'zod'
import {
  LOCAL_COPY_PROBE_TIMEOUT_MILLISECONDS,
  LOCAL_COPY_SERVED_RELEASE_FIELD,
  LOCAL_COPY_VERSION_URL,
} from './config'
import { releaseIdSchema } from '../../../scripts/ipfs-release/schema'

/**
 * What one probe found. `running` carries the release id the local server
 * says it serves; every failure is `absent`.
 */
export type LocalCopyStatus = { kind: 'absent' } | { kind: 'running'; servedReleaseId: string }

/** Only the one field this page reads; the rest of the answer passes through unread. */
const localVersionAnswerSchema = z.object({
  [LOCAL_COPY_SERVED_RELEASE_FIELD]: releaseIdSchema,
})

/** Options for {@link probeLocalCopy}. Every field has a production default. */
export interface ProbeLocalCopyOptions {
  readonly versionUrl?: string
  readonly timeoutMilliseconds?: number
  readonly fetchImplementation?: typeof fetch
}

const ABSENT: LocalCopyStatus = Object.freeze({ kind: 'absent' })

/**
 * Reads the served release id out of one parsed answer, or `null` when the
 * answer is not the shape the local server writes.
 */
const readServedReleaseId = (answer: unknown): string | null => {
  const parsed = localVersionAnswerSchema.safeParse(answer)
  if (!parsed.success) {
    return null
  }
  return parsed.data[LOCAL_COPY_SERVED_RELEASE_FIELD]
}

/**
 * Asks the local server which release it serves.
 *
 * @returns `running` with the served release id, or `absent` for every
 * failure — refused, timed out, non-200, unparsable, or missing the field.
 */
export const probeLocalCopy = async (
  options: ProbeLocalCopyOptions = {},
): Promise<LocalCopyStatus> => {
  const {
    versionUrl = LOCAL_COPY_VERSION_URL,
    timeoutMilliseconds = LOCAL_COPY_PROBE_TIMEOUT_MILLISECONDS,
    fetchImplementation = fetch,
  } = options

  try {
    const response = await fetchImplementation(versionUrl, {
      cache: 'no-store',
      signal: AbortSignal.timeout(timeoutMilliseconds),
    })
    if (!response.ok) {
      return ABSENT
    }
    const servedReleaseId = readServedReleaseId(await response.json())
    if (servedReleaseId === null) {
      return ABSENT
    }
    return { kind: 'running', servedReleaseId }
  } catch {
    return ABSENT
  }
}
